import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MonthStepper from '../../components/MonthStepper';
import { backState } from '../../components/BackLink';
import InvoicesTab from './components/InvoicesTab';
import InvoiceBatchesTab from './components/InvoiceBatchesTab';
import FinanceSectionPage from './components/FinanceSectionPage';
import InvoiceMonthSteps from './components/Invoices/InvoiceMonthSteps';
import GenerateDraftsDialog from './components/Invoices/GenerateDraftsDialog';
import IssueInvoicesDialog from './components/Invoices/IssueInvoicesDialog';
import SendInvoicesDialog from './components/Invoices/SendInvoicesDialog';
import { useFinanceMonth } from './useFinanceMonth';
import { FINANCE_INVOICES, INVOICE_CREATE } from '../../common/constants/routes.constants';
import { MESSAGE_TYPE } from '../../common/constants/common.constants';
import { InvoiceDeliveryStatusEnum, InvoiceStateFilterEnum, InvoiceStatusEnum } from '../../common/enums/invoicing.enum';
import { GetInvoicesParams, InvoiceDto, InvoiceMonthSummaryDto } from '../../common/interfaces/invoicing.interface';
import { downloadInvoicesZip, getInvoiceMonth, getInvoices } from '../../api/invoicing.api';
import { createNotification } from '../../utilities/helper/notification.helper';
import { downloadBlob } from '../../utilities/helper/download.helper';
import { currentMonthKey, formatMonthKey, parseMonthKey, shiftMonthKey } from '../../utilities/helper/date.helper';
import { useLatestRequest } from '../../utilities/hooks/useLatestRequest';

/** The month just ended: the one a kindergarten invoices at the start of the next. */
const previousMonth = () => shiftMonthKey(currentMonthKey(), -1);

/** Every invoice of the month matching `params`, across pages. */
const fetchAll = async (kindergartenId: string, month: string, params: GetInvoicesParams): Promise<InvoiceDto[]> => {
  const period = parseMonthKey(month)!;
  const all: InvoiceDto[] = [];
  for (let page = 1; ; page += 1) {
    const result = await getInvoices(kindergartenId, { ...params, periodYear: period.year, periodMonth: period.month, page, perPage: 100 });
    all.push(...(result.results ?? []));
    if (page >= (result.paginationInfo?.totalPages ?? 1)) break;
  }
  return all;
};

/**
 * Invoices for a month: make drafts, check them, issue them, send them to the parents.
 *
 * The month is the page's main filter and lives in `?month=`; it opens on the month just
 * ended. The four step cards say how far the month is, and carry the one button that
 * matters next; the list below is that month's invoices.
 */
const MonthBody: React.FC<{ kindergartenId: string }> = ({ kindergartenId }) => {
  const { t } = useTranslation();
  const [month, setMonth] = useFinanceMonth(previousMonth);
  const [summary, setSummary] = useState<InvoiceMonthSummaryDto | null>(null);
  const beginSummary = useLatestRequest();
  const [listKey, setListKey] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [issuing, setIssuing] = useState<InvoiceDto[] | null>(null);
  const [sending, setSending] = useState<InvoiceDto[] | null>(null);
  const [busy, setBusy] = useState(false);
  const monthLabel = formatMonthKey(month).toLowerCase();

  const loadSummary = useCallback(async () => {
    const period = parseMonthKey(month);
    if (!period) return;
    const isCurrent = beginSummary();
    try {
      const result = await getInvoiceMonth(kindergartenId, { periodYear: period.year, periodMonth: period.month });
      if (isCurrent()) setSummary(result.results ?? null);
    } catch {
      if (isCurrent()) setSummary(null);
    }
  }, [kindergartenId, month, beginSummary]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const refreshAll = () => {
    loadSummary();
    setListKey(key => key + 1);
  };

  const withBusy = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
    } catch {
      createNotification(MESSAGE_TYPE.ERROR, t('invoices.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const openIssue = () =>
    withBusy(async () => setIssuing(await fetchAll(kindergartenId, month, { states: [InvoiceStateFilterEnum.DRAFT] })));
  const openSend = () =>
    withBusy(async () => {
      const issued = await fetchAll(kindergartenId, month, {
        statuses: [InvoiceStatusEnum.ISSUED],
        deliveryStatuses: [InvoiceDeliveryStatusEnum.NOT_SENT, InvoiceDeliveryStatusEnum.FAILED]
      });
      setSending(issued.filter(invoice => !!invoice.payerEmail));
    });
  const downloadZip = () =>
    withBusy(async () => {
      const issued = await fetchAll(kindergartenId, month, { statuses: [InvoiceStatusEnum.ISSUED] });
      const { blob, skipped } = await downloadInvoicesZip(
        kindergartenId,
        issued.map(invoice => invoice.id)
      );
      downloadBlob(blob, `racuni-${month}.zip`);
      if (skipped) createNotification(MESSAGE_TYPE.ERROR, t('invoices.selection.zipSkipped', { count: skipped }));
    });

  return (
    <>
      <Box sx={{ mb: 2.5, '& .MuiButton-root': { fontSize: 20, fontWeight: 600 } }}>
        <MonthStepper value={month} onChange={setMonth} />
      </Box>

      {summary && (
        <InvoiceMonthSteps
          kindergartenId={kindergartenId}
          summary={summary}
          monthLabel={monthLabel}
          onGenerate={() => setGenerating(true)}
          onIssue={openIssue}
          onSend={openSend}
          onDownloadZip={downloadZip}
          busy={busy}
        />
      )}

      <InvoicesTab
        key={`${month}-${listKey}`}
        kindergartenId={kindergartenId}
        month={month}
        counts={summary?.invoices ?? null}
        onChanged={loadSummary}
      />

      {generating && (
        <GenerateDraftsDialog
          kindergartenId={kindergartenId}
          month={month}
          onClose={() => setGenerating(false)}
          onGenerated={batch => {
            createNotification(MESSAGE_TYPE.SUCCESS, t('invoices.generateMonth.done', { count: batch.createdCount }));
            const generated = `${batch.periodStart.slice(0, 7)}`;
            if (generated !== month) setMonth(generated);
            refreshAll();
          }}
        />
      )}
      {issuing && (
        <IssueInvoicesDialog
          kindergartenId={kindergartenId}
          drafts={issuing}
          monthLabel={monthLabel}
          onClose={() => setIssuing(null)}
          onDone={refreshAll}
        />
      )}
      {sending && (
        <SendInvoicesDialog kindergartenId={kindergartenId} invoices={sending} onClose={() => setSending(null)} onSent={refreshAll} />
      )}
    </>
  );
};

const FinanceInvoicesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <FinanceSectionPage
      title={t('nav.financeItems.invoices')}
      description={t('finance.pages.invoicesDescription')}
      messages={{ pickKindergarten: t('invoices.pickKindergarten'), invoicingOff: t('invoices.invoicingOff') }}
      // the same month for whichever kindergarten is picked
      kindergartenHref={id => {
        const month = new URLSearchParams(location.search).get('month');
        return `${FINANCE_INVOICES}?kindergartenId=${id}${month ? `&month=${month}` : ''}`;
      }}
      actions={({ kindergartenId, ready }) => (
        <>
          <Button
            startIcon={<HistoryIcon />}
            disabled={!ready}
            onClick={() => setHistoryOpen(true)}
            sx={{ textTransform: 'none', fontSize: 14, borderRadius: '10px' }}
          >
            {t('invoices.runs.history')}
          </Button>
          <Button
            startIcon={<AddIcon />}
            disabled={!ready}
            onClick={() =>
              navigate(
                `${INVOICE_CREATE}?kindergartenId=${kindergartenId}`,
                backState(`${location.pathname}${location.search}`, t('invoices.title'))
              )
            }
            sx={{ textTransform: 'none', fontSize: 14, borderRadius: '10px' }}
          >
            {t('invoices.single.title')}
          </Button>
          {historyOpen && (
            <Dialog open onClose={() => setHistoryOpen(false)} fullWidth maxWidth="md" slotProps={{ paper: { sx: { borderRadius: 4 } } }}>
              <DialogTitle sx={{ fontSize: 20, fontWeight: 600, pr: 6 }}>
                {t('invoices.runs.history')}
                <IconButton
                  aria-label={t('common.close')}
                  onClick={() => setHistoryOpen(false)}
                  sx={{ position: 'absolute', right: 12, top: 12 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <InvoiceBatchesTab kindergartenId={kindergartenId} />
              </DialogContent>
            </Dialog>
          )}
        </>
      )}
    >
      {({ kindergartenId }) => <MonthBody kindergartenId={kindergartenId} />}
    </FinanceSectionPage>
  );
};

export default FinanceInvoicesPage;
